

import React, {
    useEffect,
    useRef,
    useState
}
from "react";


import { useHistory } from "react-router-dom";

import HomeBox
from "../HomeBox";

import HomeBoxUtilisation
from "../HomeBoxUtilisation";

import HomeBoxDataSpace
from "./HomeBoxDataSpace";

import HomeBoxDataEnergy
from "./HomeBoxDataEnergy";

import HomeBoxDataFinancials
from "./HomeBoxDataFinancials";

import HomeBoxDataCarbon
    from "./HomeBoxDataCarbon";

import HomeBoxDataIncidents
    from "./HomeBoxDataIncidents";

import HomeDataOverview
from "./HomeDataOverview";

import {
    TRUE
}
from "../../../../constants/general-constants";

import {
    OMNISCAN_BLUE,
    OMNISCAN_BLUE_RGB
}
from "../../../../constants/colors";

import "./HomeDataScroller.css";


//========================================================
// Configuration
//========================================================

const SCROLL_INTERVAL_MS = 8000;

const FADE_DURATION_MS = 1200;


//========================================================
// Data Subjects
//
// Subject definitions are centralised here.
//
// chartTypes will be used later for the executive
// intelligence chart selection functionality.
//========================================================

const DATA_SUBJECTS =
[
    {
        id: "utilisation",
        title: "Utilisation",
        color: "#2686cf",
        component: HomeBoxUtilisation,
        chartTypes: ["total", "stack", "trend", "topten"]
    },
    {
        id: "space",
        title: "Space",
        color: "#9667c9",
        component: HomeBoxDataSpace,
        chartTypes: ["total", "stack", "trend", "topten"]
    },
    {
        id: "energy",
        title: "Energy & Utilities",
        color: "#e49a35",
        component: HomeBoxDataEnergy,
        chartTypes: ["total", "stack", "trend", "topten"]
    },
    {
        id: "financials",
        title: "Financials",
        color: "#28a788",
        component: HomeBoxDataFinancials,
        chartTypes: ["total", "stack", "trend", "topten"]
    },
	{
	    id: "carbon",
	    title: "Carbon",
	    color: "#28A788",
	    component: HomeBoxDataCarbon,
	    chartTypes: [
	        "total",
	        "stack",
	        "trend",
	        "topten",
	        "spider",
	        "funnel"
	    ]
	},
	{
	    id: "incidents",
	    title: "Incidents",
	    color: "#D65B64",
	    component: HomeBoxDataIncidents,
	    chartTypes: [
	        "total",
	        "stack",
	        "trend",
	        "topten",
	        "spider",
	        "funnel"
	    ]
	}
];


//========================================================
// Home Data Scroller
//========================================================

export default function HomeDataScroller({
    homeConfig,
    isDemo = false
})
{
	
	//========================================================
	// Navigation
	//========================================================

	const history = useHistory();

	const goUtilisation =
	    () =>
	    {
	        history.push(
	            "/homeutilisation"
	        );
	    };


	//========================================================
	// Administrator-Controlled Utilisation Link
	//========================================================

	const utilisationIsLive =
	    homeConfig?.isLiveLinkUtilisation === true
	    ||
	    homeConfig?.isLiveLinkUtilisation === "true";

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [activeSlot, setActiveSlot] =
        useState(0);

    const [slotIndexes, setSlotIndexes] =
        useState([0, null]);

    const [transitioning, setTransitioning] =
        useState(false);

    const [fadeStarted, setFadeStarted] =
        useState(false);

    const [paused, setPaused] =
        useState(false);

    const [overviewOpen, setOverviewOpen] =
        useState(false);

    const [navigationVersion, setNavigationVersion] =
        useState(0);


    //----------------------------------------------------
    // References
    //----------------------------------------------------

    const transitionTimer =
        useRef(null);

    const frameOne =
        useRef(null);

    const frameTwo =
        useRef(null);


    //----------------------------------------------------
    // Current Subject
    //----------------------------------------------------

    const activeIndex =
        slotIndexes[activeSlot] ?? 0;

    const currentSubject =
        DATA_SUBJECTS[activeIndex];


    //========================================================
    // Reset
    //========================================================

    useEffect(
        function()
        {

            setActiveSlot(0);
            setSlotIndexes([0, null]);

            setTransitioning(false);
            setFadeStarted(false);

            setPaused(false);
            setOverviewOpen(false);

            setNavigationVersion(
                function(previous)
                {
                    return previous + 1;
                }
            );

        },
        [isDemo]
    );


    //========================================================
    // Cleanup On Unmount
    //========================================================

    useEffect(
        function()
        {

            return function()
            {

                clearTimeout(transitionTimer.current);

                cancelAnimationFrame(frameOne.current);
                cancelAnimationFrame(frameTwo.current);

            };

        },
        []
    );


    //========================================================
    // Navigate To Subject
    //========================================================

    const navigateTo =
        function(index)
        {

            //------------------------------------------------
            // Ignore Invalid Or Unnecessary Navigation
            //------------------------------------------------

            if(
                !isDemo
                ||
                transitioning
                ||
                index === activeIndex
                ||
                index < 0
                ||
                index >= DATA_SUBJECTS.length
            )
            {
                return;
            }


            //------------------------------------------------
            // Determine Incoming Slot
            //------------------------------------------------

            const incomingSlot =
                activeSlot === 0 ? 1 : 0;


            //------------------------------------------------
            // Prepare Incoming Component
            //------------------------------------------------

            setSlotIndexes(
                function(previous)
                {

                    const updated =
                        [...previous];

                    updated[incomingSlot] =
                        index;

                    return updated;

                }
            );

            setTransitioning(true);
            setFadeStarted(false);

        };


    //========================================================
    // Start Crossfade
    //
    // Allow the incoming component to mount at opacity 0
    // before beginning the transition.
    //========================================================

    useEffect(
        function()
        {

            if(!transitioning)
            {
                return;
            }

            frameOne.current =
                requestAnimationFrame(
                    function()
                    {

                        frameTwo.current =
                            requestAnimationFrame(
                                function()
                                {

                                    setFadeStarted(true);

                                }
                            );

                    }
                );


            //------------------------------------------------
            // Cleanup
            //------------------------------------------------

            return function()
            {

                cancelAnimationFrame(frameOne.current);
                cancelAnimationFrame(frameTwo.current);

            };

        },
        [transitioning]
    );


    //========================================================
    // Complete Crossfade
    //
    // The incoming component remains mounted.
    // Only the active slot changes.
    //========================================================

    useEffect(
        function()
        {

            if(!fadeStarted)
            {
                return;
            }

            transitionTimer.current =
                setTimeout(
                    function()
                    {

                        setActiveSlot(
                            function(previous)
                            {
                                return previous === 0 ? 1 : 0;
                            }
                        );

                        setTransitioning(false);
                        setFadeStarted(false);

                        setNavigationVersion(
                            function(previous)
                            {
                                return previous + 1;
                            }
                        );

                    },
                    FADE_DURATION_MS + 50
                );


            //------------------------------------------------
            // Cleanup
            //------------------------------------------------

            return function()
            {

                clearTimeout(transitionTimer.current);

            };

        },
        [fadeStarted]
    );


    //========================================================
    // Automatic Rotation
    //========================================================

    useEffect(
        function()
        {

            if(
                !isDemo
                ||
                paused
                ||
                overviewOpen
                ||
                transitioning
            )
            {
                return;
            }

            const timer =
                setTimeout(
                    function()
                    {

                        navigateTo(
                            (activeIndex + 1) % DATA_SUBJECTS.length
                        );

                    },
                    SCROLL_INTERVAL_MS
                );


            //------------------------------------------------
            // Cleanup
            //------------------------------------------------

            return function()
            {

                clearTimeout(timer);

            };

        },
        [
            isDemo,
            paused,
            overviewOpen,
            transitioning,
            activeIndex,
            navigationVersion
        ]
    );


    //========================================================
    // Render Subject
    //========================================================

    const renderSubject =
        function(index)
        {

            const subject =
                DATA_SUBJECTS[index];

            if(!subject)
            {
                return null;
            }


            //------------------------------------------------
            // Existing Subject Component
            //------------------------------------------------

            if(subject.component)
            {

                const Component =
                    subject.component;

                return (

                    <Component
                        homeConfig={homeConfig}
                    />

                );

            }


            //------------------------------------------------
            // Temporary Placeholder
            //
            // Carbon and Incidents will receive
            // their real chart components later.
            //------------------------------------------------

            return (

                <div
                    className="home-data-placeholder"

                    style={{
                        background: subject.color
                    }}
                >

                    {subject.title.toUpperCase()}

                </div>

            );

        };


    //========================================================
    // Layer Opacity
    //========================================================

    const getLayerOpacity =
        function(slot)
        {

            if(!fadeStarted)
            {
                return slot === activeSlot ? 1 : 0;
            }

            return slot === activeSlot ? 0 : 1;

        };


    //========================================================
    // Render
    //========================================================

    return (

        <>

            {/* ==========================================
                PERMANENT HOME BOX
            ========================================== */}

            <HomeBox
			isLive={
			    currentSubject.id === "utilisation"
			    &&
			    utilisationIsLive
			}

			clickFunction={
			    currentSubject.id === "utilisation"
			        ? goUtilisation
			        : undefined
			}
                title={currentSubject.title}
                color={OMNISCAN_BLUE}
                colorRgb={OMNISCAN_BLUE_RGB}
                icon="chart-bar"
                isLast={TRUE}
                delay={1080}
            >

                <div
                    className="home-data-scroller"

                    onMouseEnter={
                        function()
                        {

                            if(isDemo)
                            {
                                setPaused(true);
                            }

                        }
                    }

                    onMouseLeave={
                        function()
                        {
                            setPaused(false);
                        }
                    }
                >

                    {/* ==================================
                        TITLE AND TOP-RIGHT NAVIGATION
                    ================================== */}

                    <div className="home-data-scroller-title">

                        {/* ==============================
                            SUBJECT TITLE
                        ============================== */}

                        <span>

                            {currentSubject.title}

                        </span>


                        {/* ==============================
                            DEMO NAVIGATION
                        ============================== */}

                        {
                            isDemo
                            &&
                            <div className="home-data-navigation">

                                {/* ======================
                                    SUBJECT DOTS
                                ====================== */}

                                <div className="home-data-dots">

                                    {
                                        DATA_SUBJECTS.map(
                                            function(subject, index)
                                            {

                                                const selected =
                                                    index === activeIndex;

                                                return (

                                                    <button
                                                        key={subject.id}

                                                        type="button"

                                                        className={
                                                            "home-data-dot"
                                                            +
                                                            (
                                                                selected
                                                                    ?
                                                                    " selected"
                                                                    :
                                                                    ""
                                                            )
                                                        }

                                                        style={{
                                                            "--dot-color":
                                                                subject.color
                                                        }}

                                                        title={subject.title}

                                                        aria-label={
                                                            "Show "
                                                            +
                                                            subject.title
                                                        }

                                                        aria-current={
                                                            selected
                                                                ?
                                                                "true"
                                                                :
                                                                undefined
                                                        }

                                                        disabled={transitioning}

                                                        onClick={
                                                            function()
                                                            {
                                                                navigateTo(index);
                                                            }
                                                        }
                                                    />

                                                );

                                            }
                                        )
                                    }

                                </div>


                                {/* ======================
                                    VIEW ALL BUTTON
                                ====================== */}

                                <button
                                    type="button"

                                    className="home-data-view-all"

                                    onClick={
                                        function()
                                        {
                                            setOverviewOpen(true);
                                        }
                                    }
                                >

                                    View All

                                    <span aria-hidden="true">
                                        ↗
                                    </span>

                                </button>

                            </div>
                        }

                    </div>


                    {/* ==================================
                        CHART STAGE
                    ================================== */}

                    <div className="home-data-scroller-stage">

                        {
                            slotIndexes.map(
                                function(index, slot)
                                {

                                    if(index === null)
                                    {
                                        return null;
                                    }

                                    return (

                                        <div
                                            key={slot}

                                            className="home-data-layer"

                                            style={{
                                                opacity:
                                                    getLayerOpacity(slot),

                                                zIndex:
                                                    slot === activeSlot
                                                        ?
                                                        1
                                                        :
                                                        2,

                                                pointerEvents:
                                                    slot === activeSlot
                                                    && !fadeStarted
                                                        ?
                                                        "auto"
                                                        :
                                                        "none"
                                            }}
                                        >

                                            {renderSubject(index)}

                                        </div>

                                    );

                                }
                            )
                        }

                    </div>

                </div>

            </HomeBox>


            {/* ==========================================
                EXECUTIVE INTELLIGENCE OVERVIEW
            ========================================== */}

            {
                isDemo
                &&
                <HomeDataOverview
                    open={overviewOpen}

                    subjects={DATA_SUBJECTS}

                    onClose={
                        function()
                        {
                            setOverviewOpen(false);
                        }
                    }
                />
            }

        </>

    );

}
